import * as Gsap from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import * as Topics from "../Data/Topics";
import { IOnboardingTarget } from "../GameObjects/IOnboardingTarget";
import { InteruptType, VoiceCategory } from "./VoiceOverManager";

export class Button extends HLF.Button implements HLF.Input.IFocusable, IOnboardingTarget {
    protected static readonly SCALE_NORMAL: number = 1.0;
    protected static readonly SCALE_OVER: number = 1.05;
    protected static readonly SCALE_DOWN: number = 0.95;

    protected _hoverVO: string | undefined;
    protected _clickedSFX: string | undefined;
    protected _clickedSFXVolume: number = 1;

    constructor(pCallback: () => void, pAsset?: string, pSheet?: string, pHoverVO?: string, pClickedSFX?: string) {
        super(pCallback, pAsset, pSheet);

        this._hoverVO = pHoverVO;
        this._clickedSFX = pClickedSFX;
    }

    public onFocusBegin(): void {
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_BEGIN, this);
        this.playHoverVO();
    }

    public onFocusActivated(): void {
        if (this.interactive) {
            this._callback();
            this.playClickedSFX();
        }
    }

    public onFocusEnd(): void {
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_END, this);
    }

    public getFocusPosition(): PIXI.Point {
        return this.getGlobalPosition();
    }

    public getFocusSize(): PIXI.Point {
        return new PIXI.Point(this.width, this.height);
    }

    public getOnboardingPosition(): PIXI.Point {
        return this.getGlobalPosition();
    }

    public getOnboardingRotation(): number {
        return 0;
    }

    public setVisuals(pVisual: PIXI.Container): void {
        this._visuals = pVisual;
    }

    public setAndAddIcon(pAsset: string, pSheet: string) {
        this.setIcon(pAsset, pSheet);

        if (this._image !== undefined) {
            this._image.addChild(this._icon!);
        }
        else {
            this.addChild(this._icon!);
        }
    }

    public setIcon(pAsset: string, pSheet: string) {
        this._icon = HLF.PixiUtils.makeSprite(pAsset, pSheet);
    }

    public setText(pText: string): void {
        if (this._text !== undefined) {
            this._text.text = pText;
        }
    }

    public simulateClick(): void {
        const clickTimeline: Gsap.TimelineLite = new Gsap.TimelineLite();
        clickTimeline.call(this.scaleDown, undefined, this, 0);
        clickTimeline.call(() => {
            this.scaleOver();
            this.playClickedSFX();
        }, undefined, this, 0.15);
    }

    public scaleNormal(): void {
        Gsap.TweenMax.to(this._visuals.scale, 0.15, {
            x: Button.SCALE_NORMAL,
            y: Button.SCALE_NORMAL,
            ease: Gsap.Sine.easeInOut,
        });
    }

    protected onPointerOver(pEvent: PIXI.interaction.InteractionEvent): void {
        super.onPointerOver(pEvent);
        this.scaleOver();
        this.playHoverVO();
    }

    protected onPointerDown(pEvent: PIXI.interaction.InteractionEvent): void {
        super.onPointerDown(pEvent);
        this.playClickedSFX();
        this.scaleDown();
    }

    protected onPointerUp(pEvent: PIXI.interaction.InteractionEvent): void {
        if (this._eventData !== undefined && this._eventData.identifier === pEvent.data.identifier) {
            this.scaleOver();
        }
        super.onPointerUp(pEvent);
    }

    protected onPointerOut(pEvent: PIXI.interaction.InteractionEvent): void {
        super.onPointerOut(pEvent);
        this.scaleNormal();
    }

    protected playHoverVO(): boolean {
        if (this._hoverVO !== undefined) {
            return Application.instance.voiceOver.play(this._hoverVO, InteruptType.EQUAL, VoiceCategory.UI);
        }
        return false;
    }

    protected playClickedSFX(): void {
        if (this._clickedSFX !== undefined) {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(
                this._clickedSFX,
                this._clickedSFXVolume,
                false, HLF.AudioCategory.SFX,
            ));
        }
    }

    protected scaleDown(): void {
        Gsap.TweenMax.to(this._visuals.scale, 0.15, {
            x: Button.SCALE_DOWN,
            y: Button.SCALE_DOWN,
            ease: Gsap.Sine.easeInOut,
        });
    }

    protected scaleOver(): void {
        Gsap.TweenMax.to(this._visuals.scale, 0.15, {
            x: Button.SCALE_OVER,
            y: Button.SCALE_OVER,
            ease: Gsap.Sine.easeInOut,
        });
    }
}
